/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */
import Messenger from "App/messenger";

export enum ELogType {
    INFO = 0,
    WARNING = 1,
    ERROR = 2,
    EXCEPTION = 3
}

export const INFO: string = "INFO";
export const WARNING: string = "WARNING";
export const ERROR: string = "ERROR";
export const EXCEPTION: string = "EXCEPTION";

export let toggleShowDebugConsole: boolean = false;
export const logToConsole: boolean = true;

// stores the number of lines logged
export let numLinesLogged: number = 0;

// the maximum number of lines to log in order to prevent
// the browser to slow down
export const maxLinesToLog: number = 10000;

// the container div for the logging messages
export const logContainer = function(): HTMLDivElement {
    let element = document.createElement("div");
    element.id = "cwEngine_logdiv";
    element.setAttribute("class", "cwEngine-logContainer");
    element.style.clear = "both";

    return element;
}();

/**
 * This functions passes messages to our own processing.
 *
 * @param {string} msg the log message
 * @param {enum} logType the type of the log message. One of INFO, WARNING, ERROR or EXCEPTION
 */
export function cwPassMessage(msg: string, logType: ELogType): void {
    switch (logType) {
        case ELogType.INFO:
            // console.info(msg);
            break;
        case ELogType.WARNING:
            // console.warn(msg);
            break;
        case ELogType.ERROR:
        case ELogType.EXCEPTION:
            // @ts-ignore
            Messenger.fireCDWKEvent(document, "cwx3d_engineError", {
                type: logType,
                msg: msg
            });
            break;
        default:
            break;
    }
}

/**
 * @brief Activates the log
 */
export function activate(visible: boolean): void {
    logContainer.style.display = (visible) ? "block" : "none";
}

/**
 * @brief Generic logging function which does all the work.
 * @param msg the log message
 * @param logType the type of the log message. One of INFO, WARNING, ERROR
 * or EXCEPTION.
 */
export function doLog(msg: string, logType: ELogType): void {
    // If we have reached the maximum number of logged lines output
    // a warning message
    if (numLinesLogged === maxLinesToLog) {
        msg = "Maximum number of log lines (=" + maxLinesToLog +
            ") reached. Deactivating logging...";
    }

    // If the maximum number of log lines is exceeded do not log anything
    // but simply return
    if (numLinesLogged > maxLinesToLog) {
        return;
    }

    // Output a log line to the HTML page
    let node: any = document.createElement("p");
    node.style.margin = "0px";

    switch (logType) {
        case ELogType.INFO:
/////////////////////
            node.style.color = "#00ff00";
            // @ts-ignore
            if (logToConsole) {
                console.log("INFO: " + msg);
            }
/////////////////////
            break;
        case ELogType.WARNING:
            node.style.color = "#cd853f";
            if (logToConsole) {
                console.warn("WARNING: " + msg);
            }
            break;
        case ELogType.ERROR:
            node.style.color = "#ff4500";
            if (logToConsole) {
                console.error("ERROR: " + msg);
            }
            break;
        case ELogType.EXCEPTION:
            node.style.color = "#ffff00";
            if (logToConsole) {
                console.error("EXCEPTION: " + msg);
            }
            break;
        default:
            node.style.color = "#00ff00";
            if (logToConsole) {
                console.error("UNDEFINED[" + logType + "]: " + msg);
            }
            break;
    }

    try {
        node.innerHTML = logType + ": " + msg;
        logContainer.insertBefore(node, logContainer.firstChild);
    } catch (err) {
        // @ts-ignore
        if (window.console.firebug !== undefined) {
            window.console.warn(msg);
        }
    }

    numLinesLogged++;

    // pass messages to cadwork for processing
    cwPassMessage(msg, logType);
}

/** Log an info message. */
export function logInfo(msg: string): void {
    doLog(msg, ELogType.INFO);
}

/** Log a warning message. */
export function logWarning(msg: string): void {
    doLog(msg, ELogType.WARNING);
}

/** Log an error message. */
export function logError(msg: string): void {
    doLog(msg, ELogType.ERROR);
}

/** Log an exception message. */
export function logException(msg: string): void {
    doLog(msg, ELogType.EXCEPTION);
}

/** Log an assertion. */
export function assert(c: any, msg: string): void {
    if (!c) {
        // @ts-ignore
        doLog("Assertion failed in " + assert.caller.name + ': ' +
            msg, ELogType.ERROR);
    }
}
